import type { ClientMessage, ServerMessage } from '../shared/protocol';

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export type ConnectionHandlers = {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
};

/**
 * WebSocket wrapper with reconnect.
 *
 * The client holds no authoritative state (§11) — on every reconnect the server
 * sends a full `view`, so there is nothing to replay or merge. That makes
 * reconnection a matter of reopening the socket and waiting.
 */
export class Connection {
  readonly #url: string;
  readonly #handlers: ConnectionHandlers;
  #ws: WebSocket | null = null;
  #retries = 0;
  #timer: number | null = null;
  #closedByUs = false;

  constructor(tableId: string, handlers: ConnectionHandlers) {
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    this.#url = `${scheme}://${location.host}/ws/${encodeURIComponent(tableId)}`;
    this.#handlers = handlers;
  }

  open(): void {
    this.#closedByUs = false;
    this.#handlers.onStatus('connecting');

    const ws = new WebSocket(this.#url);
    this.#ws = ws;

    ws.addEventListener('open', () => {
      this.#retries = 0;
      this.#handlers.onStatus('open');
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        this.#handlers.onMessage(JSON.parse(event.data) as ServerMessage);
      } catch {
        // A frame we cannot parse is not worth tearing the connection down for.
      }
    });

    ws.addEventListener('close', () => {
      this.#ws = null;
      this.#handlers.onStatus('closed');
      if (!this.#closedByUs) this.#scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // 'close' always follows; reconnect is handled there.
    });
  }

  send(message: ClientMessage): void {
    if (this.#ws?.readyState !== WebSocket.OPEN) return;
    this.#ws.send(JSON.stringify(message));
  }

  close(): void {
    this.#closedByUs = true;
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#ws?.close();
  }

  /** Exponential backoff, capped, with jitter so six players don't sync up. */
  #scheduleReconnect(): void {
    this.#retries += 1;
    const base = Math.min(500 * 2 ** (this.#retries - 1), 10_000);
    const delay = base * (0.7 + Math.random() * 0.6);
    this.#timer = setTimeout(() => this.open(), delay) as unknown as number;
  }
}
